import { post } from 'aws-amplify/api';
import { ulid } from 'ulidx';
import { AudioFileCategory, AudioFileUploadRequest, AudioFileUploadResponse, AudioFileVersion } from '@aviation/cabinapp-cabincast-sdk';

export const AUDIO_FILE_CATEGORIES: { value: AudioFileCategory, label: string }[] = [
    { value: 'music', label: 'Music' },
    { value: 'safety', label: 'Safety' },
    { value: 'service', label: 'Service' },
    { value: 'emergency', label: 'Emergency' },
];

export const DEFAULT_LANGUAGE = 'EN';

export const AUDIO_FILE_LANGUAGES: { code: string, label: string }[] = [
    { code: 'EN', label: 'English' },
    { code: 'DE', label: 'German' },
    { code: 'FR', label: 'French' },
    { code: 'ES', label: 'Spanish' },
    { code: 'IT', label: 'Italian' },
    { code: 'NL', label: 'Dutch' },
    { code: 'PT', label: 'Portuguese' },
    { code: 'PL', label: 'Polish' },
    { code: 'SV', label: 'Swedish' },
    { code: 'DA', label: 'Danish' },
    { code: 'NB', label: 'Norwegian' },
    { code: 'FI', label: 'Finnish' },
    { code: 'EL', label: 'Greek' },
    { code: 'TR', label: 'Turkish' },
    { code: 'CS', label: 'Czech' },
    { code: 'HU', label: 'Hungarian' },
    { code: 'RU', label: 'Russian' },
];

export function formatDuration(seconds?: number): string {
    if (seconds === undefined || seconds === null || isNaN(seconds)) return '';
    const total = Math.round(seconds);
    const mm = Math.floor(total / 60).toString().padStart(2, '0');
    const ss = (total % 60).toString().padStart(2, '0');
    return `${mm}:${ss}`;
}

/** Reads the duration (seconds) of a local audio file. */
export function readAudioDuration(file: File): Promise<number | undefined> {
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const audio = new Audio();
        const done = (value?: number) => {
            URL.revokeObjectURL(url);
            resolve(value);
        };
        audio.preload = 'metadata';
        audio.onloadedmetadata = () => done(isFinite(audio.duration) ? Math.round(audio.duration) : undefined);
        audio.onerror = () => done(undefined);
        audio.src = url;
    });
}

/** Uploads a single file to S3 via a presigned URL and returns the matching version object. */
export async function uploadAudioFileVersion(audioFileId: string, file: File, language: string): Promise<AudioFileVersion> {
    const request: AudioFileUploadRequest = { Id: audioFileId, FileId: ulid() };
    const response = await post({ path: '/api/audiofiles/upload', apiName: 'api', options: { body: request as any } }).response;
    const { Url } = await response.body.json() as unknown as AudioFileUploadResponse;
    if (!Url) throw new Error(`No upload URL received for ${file.name}`);

    const upload = await fetch(Url, { method: 'PUT', body: file });
    if (!upload.ok) throw new Error(`Upload of ${file.name} failed (${upload.status})`);

    return {
        Id: request.FileId,
        AudioFileId: audioFileId,
        Filename: file.name,
        Language: language,
        Duration: await readAudioDuration(file),
    };
}
